import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import Swal from 'sweetalert2';

import { SousBien } from 'app/models/sousBien';
import { SousBienService } from 'app/services/sousBiens/sous-bien.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-modifier-sous-immob',
  templateUrl: './modifier-sous-immob.component.html',
  styleUrls: ['./modifier-sous-immob.component.scss']
})
export class ModifierSousImmobComponent implements OnInit {
  @ViewChild('descriptionTextarea', { static: false }) descriptionTextarea!: ElementRef;
  @ViewChild('mainImageInput', { static: false }) mainImageInput!: ElementRef;
  @ViewChild('imagesListInput', { static: false }) imagesListInput!: ElementRef;

  public sousBiensadd: any;
  public idBien: any; // This is the sous bien ID from route
  public parentBienId: string | null = null; // Parent bien ID for navigation
  public sousBien: SousBien;
  public selectedFiles: FileList | null = null;
  
  // Images
  public allImages: string[] = [];
  public mainImage: string = '';
  public originalMainImage: string = '';
  public originalImagesList: string[] = [];
  
  // Loading states
  public isLoadingData: boolean = false;
  public isUploading: boolean = false;
  public isSubmitting: boolean = false;
  public isDeletingImage: boolean = false;
  
  dataSource = new MatTableDataSource();

  constructor(
    private sousBienService: SousBienService,
    private awsService: AwscontrolService,
    private router: Router,
    private _location: Location,
    private activatedRoute: ActivatedRoute
  ) {
    this.sousBien = new SousBien();
    this.sousBien.description = '';
  }

  ngOnInit(): void {
    this.getData();
    this.checkAwsConnection();
  }

  checkAwsConnection(): void {
    this.awsService.checkHealth().subscribe({
      next: (response) => {
        console.log('✅ AWS S3 Connection Healthy:', response);
      },
      error: (err) => {
        console.warn('⚠️ AWS S3 Connection Warning:', err);
      }
    });
  }

  getData(): void {
    this.idBien = this.activatedRoute.snapshot.paramMap.get('id');
    
    if (!this.idBien) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'ID du sous bien non trouvé',
        confirmButtonColor: '#f44336'
      }).then(() => {
        this._location.back();
      });
      return;
    }

    this.isLoadingData = true;

    this.sousBienService.getSousBien(this.idBien).subscribe(
      data => {
        console.log('SousBien data loaded:', data);
        this.sousBien = data;
        
        // Extract parent bien ID
        const dataAny: any = data;
        if (dataAny.bienId) {
          this.parentBienId = dataAny.bienId.toString();
        } else if (dataAny.bien && dataAny.bien.id) {
          this.parentBienId = dataAny.bien.id.toString();
        } else if (dataAny.produit_id) { // Sometimes it's produit_id
          this.parentBienId = dataAny.produit_id.toString();
        }
        
        // Charger l'image principale
        if (dataAny.image_url) {
          this.mainImage = dataAny.image_url;
          this.originalMainImage = dataAny.image_url;
        }
        
        // Charger la liste d'images
        if (dataAny.imagesList && Array.isArray(dataAny.imagesList)) {
          this.allImages = [...dataAny.imagesList];
          this.originalImagesList = [...dataAny.imagesList];
        } else if (dataAny.images && Array.isArray(dataAny.images)) {
          this.allImages = [...dataAny.images];
          this.originalImagesList = [...dataAny.images];
        }
        
        console.log('Images loaded - Main:', this.mainImage, 'List:', this.allImages);
        console.log('Parent Bien ID:', this.parentBienId);
        this.isLoadingData = false;
      },
      error => {
        console.error('Error loading SousBien:', error);
        this.isLoadingData = false;
        Swal.fire({
          icon: 'error',
          title: 'Erreur de chargement',
          text: 'Impossible de charger les données du sous bien',
          confirmButtonColor: '#f44336'
        }).then(() => {
          this._location.back();
        });
      }
    );
  }

  // Formatage du texte dans la description
  formatText(command: string): void {
    if (!this.descriptionTextarea) return;
    
    const textarea = this.descriptionTextarea.nativeElement;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.sousBien.description.substring(start, end);
    
    if (selectedText.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sélectionnez du texte',
        text: 'Veuillez sélectionner le texte que vous souhaitez formater.',
        timer: 2000,
        showConfirmButton: false
      });
      return;
    }
    
    let formattedText = '';
    switch (command) {
      case 'bold':
        formattedText = `<strong>${selectedText}</strong>`;
        break;
      case 'italic':
        formattedText = `<em>${selectedText}</em>`;
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        break;
    }
    
    const newText = this.sousBien.description.substring(0, start) + 
                   formattedText + 
                   this.sousBien.description.substring(end);
    
    this.sousBien.description = newText;
    
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + formattedText.length;
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  }

  // Validation de fichier image
  private validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    
    if (!validTypes.includes(file.type)) {
      return { 
        valid: false, 
        error: `Le fichier ${file.name} n'est pas une image valide. Formats acceptés: JPG, PNG, GIF, WebP.` 
      };
    }
    
    if (file.size > 5 * 1024 * 1024) {
      return { 
        valid: false, 
        error: `L'image ${file.name} dépasse 5MB. Taille maximum: 5MB.` 
      };
    }
    
    return { valid: true };
  }

  // Upload liste d'images
  selectFileimgListe(e: any): void {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const totalImages = this.allImages.length + files.length;
    if (totalImages > 30) {
      Swal.fire({
        icon: 'warning',
        title: 'Limite atteinte',
        text: `Maximum 30 images autorisées. Vous avez déjà ${this.allImages.length} image(s).`,
        confirmButtonColor: '#f44336'
      });
      e.target.value = '';
      return;
    }

    const filesToUpload: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const validation = this.validateImageFile(file);
      
      if (!validation.valid) {
        Swal.fire({
          icon: 'warning',
          title: 'Fichier invalide',
          text: validation.error,
          confirmButtonColor: '#f44336'
        });
        continue;
      }
      filesToUpload.push(file);
    }

    if (filesToUpload.length > 0) {
      this.uploadMultipleImagesToList(filesToUpload);
    }
    
    e.target.value = '';
  }

  uploadMultipleImagesToList(files: File[]): void {
    this.isUploading = true;

    this.awsService.uploadFiles(files, `sous-bien-images/${this.idBien}`).subscribe({
      next: (response) => {
        this.isUploading = false;
        
        if (response.success && response.urls && response.urls.length > 0) {
          this.allImages.push(...response.urls);
          
          Swal.fire({
            icon: "success",
            title: "Images ajoutées",
            text: `${response.urls.length} image(s) ajoutée(s) avec succès`,
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploading = false;
        console.error('❌ Error uploading images:', err);
        
        Swal.fire({
          icon: "error",
          title: "Échec de l'upload",
          text: "Les images n'ont pas pu être uploadées.",
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  // Upload image principale
  selectFileimg(e: any): void {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const validation = this.validateImageFile(file);
    
    if (!validation.valid) {
      Swal.fire({
        icon: 'warning',
        title: 'Fichier invalide',
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      e.target.value = '';
      return;
    }
    
    this.uploadMainImage(file);
    e.target.value = '';
  }

  uploadMainImage(file: File): void {
    this.isUploading = true;

    this.awsService.uploadFile(file, `sous-bien-main/${this.idBien}`).subscribe({
      next: (response) => {
        this.isUploading = false;
        
        if (response.success && response.urls && response.urls.length > 0) {
          const uploadedUrl = response.urls[0];
          console.log('✅ Main image uploaded:', uploadedUrl);
          this.mainImage = uploadedUrl;
          
          Swal.fire({
            icon: "success",
            title: "Image principale mise à jour",
            text: `${file.name} a été uploadée avec succès`,
            timer: 1500,
            showConfirmButton: false
          });
        } else {
          throw new Error(response.error || 'Upload failed');
        }
      },
      error: (err) => {
        this.isUploading = false;
        console.error('❌ Error uploading main image:', err);
        
        Swal.fire({
          icon: "error",
          title: "Échec de l'upload",
          text: `Échec du téléchargement de ${file.name}.`,
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  // Gestion des images
  removeImage(index: number): void {
    const imageUrl = this.allImages[index];
    
    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer cette image de AWS S3?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteImageFromS3(imageUrl, index, 'list');
      }
    });
  }

  removeMainImage(): void {
    if (!this.mainImage || this.mainImage.trim() === '') return;
    
    Swal.fire({
      title: 'Êtes-vous sûr?',
      text: "Voulez-vous vraiment supprimer l'image principale de AWS S3?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, supprimer!',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteImageFromS3(this.mainImage, -1, 'main');
      }
    });
  }

  private deleteImageFromS3(imageUrl: string, index: number, type: 'main' | 'list'): void {
    this.isDeletingImage = true;
    
    this.awsService.deleteFile(imageUrl).subscribe({
      next: (response) => {
        this.isDeletingImage = false;
        console.log('✅ Image deleted from S3:', response);
        
        if (type === 'list' && index >= 0) {
          this.allImages.splice(index, 1);
        } else if (type === 'main') {
          this.mainImage = '';
        }
        
        Swal.fire({
          icon: "success",
          title: "Image supprimée",
          text: "L'image a été supprimée de AWS S3",
          timer: 1500,
          showConfirmButton: false
        });
      },
      error: (err) => {
        this.isDeletingImage = false;
        console.error('❌ Error deleting image:', err);
        
        Swal.fire({
          icon: "error",
          title: "Échec de la suppression",
          text: "Impossible de supprimer l'image de AWS S3",
          confirmButtonColor: '#f44336'
        });
      }
    });
  }

  // Tri des images
  moveImageUp(index: number): void {
    if (index > 0) {
      const temp = this.allImages[index];
      this.allImages[index] = this.allImages[index - 1];
      this.allImages[index - 1] = temp;
    }
  }

  moveImageDown(index: number): void {
    if (index < this.allImages.length - 1) {
      const temp = this.allImages[index];
      this.allImages[index] = this.allImages[index + 1];
      this.allImages[index + 1] = temp;
    }
  }

  // Validation du formulaire (role et website supprimés)
  private validateForm(): { valid: boolean; error?: string } {
    if (!this.sousBien.name || this.sousBien.name.trim() === '') {
      return { valid: false, error: "Le nom est obligatoire." };
    }

    if (!this.sousBien.description || this.sousBien.description.trim() === '') {
      return { valid: false, error: "La description est obligatoire." };
    }

    if (!this.mainImage && !this.sousBien.image_url) {
      return { valid: false, error: "L'image principale est obligatoire." };
    }

    return { valid: true };
  }

  // Clean up images that were uploaded but not used
  private async cleanupUnusedImages(): Promise<void> {
    const promises: Promise<void>[] = [];
    
    if (this.originalMainImage && 
        this.originalMainImage !== this.mainImage && 
        this.originalMainImage.trim() !== '') {
      promises.push(this.deleteImagePromise(this.originalMainImage));
    }
    
    const removedImages = this.originalImagesList.filter(
      originalImage => !this.allImages.includes(originalImage)
    );
    
    removedImages.forEach(imageUrl => {
      if (imageUrl && imageUrl.trim() !== '') {
        promises.push(this.deleteImagePromise(imageUrl));
      }
    });
    
    try {
      await Promise.all(promises);
      console.log('✅ Cleaned up unused images');
    } catch (error) {
      console.warn('⚠️ Some images could not be cleaned up:', error);
    }
  }

  private deleteImagePromise(imageUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.awsService.deleteFile(imageUrl).subscribe({
        next: () => resolve(),
        error: (err) => {
          console.warn('Could not delete image:', imageUrl, err);
          resolve(); // Don't reject, continue anyway
        }
      });
    });
  }

  // Update method
  updateSousImmob(): void {
    const validation = this.validateForm();
    
    if (!validation.valid) {
      Swal.fire({
        icon: "warning",
        title: "Informations manquantes",
        text: validation.error,
        confirmButtonColor: '#f44336'
      });
      return;
    }

    // Clean up unused images before update
    this.cleanupUnusedImages().then(() => {
      // Préparer les données pour l'update (sans role ni website)
      this.sousBiensadd = {
        name: this.sousBien.name.trim(),
        category: this.sousBien.category || '',
        description: this.sousBien.description.trim(),
        image_url: this.mainImage || this.sousBien.image_url,
        imagesList: this.allImages,
        prix: this.sousBien.prix || ''
        // role et website supprimés
      };

      console.log('Updating with data:', this.sousBiensadd);

      this.isSubmitting = true;

      Swal.fire({
        title: 'Mise à jour en cours...',
        text: 'Veuillez patienter',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      this.sousBienService.updateSousBien(this.idBien, this.sousBiensadd).subscribe(
        (response: any) => {
          this.isSubmitting = false;
          Swal.close();
          console.log('SousBien updated successfully:', response);
          
          this.originalMainImage = this.mainImage;
          this.originalImagesList = [...this.allImages];
          
          Swal.fire({
            icon: "success",
            title: "Succès",
            text: "Le sous bien a été mis à jour avec succès!",
            confirmButtonColor: '#4caf50'
          }).then(() => {
            this.goToParentList();
          });
          
          this.dataSource.data = this.dataSource.data.map((o: SousBien) => {
            if (o.id === response.id) {
              o = response;
            }
            return o;
          });
        },
        (error) => {
          this.isSubmitting = false;
          Swal.close();
          console.error('Error updating SousBien:', error);
          
          let errorMessage = 'Échec de la mise à jour du sous bien. Veuillez réessayer.';
          
          if (error.error) {
            if (typeof error.error === 'string') {
              errorMessage = error.error;
            } else if (error.error.message) {
              errorMessage = error.error.message;
            }
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          Swal.fire({
            icon: "error",
            title: "Échec de la mise à jour",
            text: errorMessage,
            confirmButtonColor: '#f44336'
          });
        }
      );
    });
  }

  // Navigation: go to list of sous biens for this parent bien
  private goToParentList(): void {
    if (this.parentBienId) {
      this.router.navigate(['/list-agence-sous-bien', this.parentBienId]);
    } else {
      this._location.back();
    }
  }

  goBack(): void {
    this.goToParentList();
  }

  // Reset form
  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser les modifications?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, réinitialiser',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.getData();
        
        if (this.mainImageInput) {
          this.mainImageInput.nativeElement.value = '';
        }
        if (this.imagesListInput) {
          this.imagesListInput.nativeElement.value = '';
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Formulaire réinitialisé',
          text: 'Les modifications ont été annulées',
          timer: 1500,
          showConfirmButton: false
        });
      }
    });
  }

  getCurrentMainImage(): string {
    return this.mainImage || 'assets/images/default-image.png';
  }

  hasUnsavedChanges(): boolean {
    return (
      this.mainImage !== this.originalMainImage ||
      JSON.stringify(this.allImages) !== JSON.stringify(this.originalImagesList) ||
      this.sousBien.name !== this.sousBien.name ||
      this.sousBien.description !== this.sousBien.description
    );
  }

  canDeactivate(): boolean {
    if (this.hasUnsavedChanges()) {
      return confirm('Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter?');
    }
    return true;
  }
}